import React, { useContext, useEffect, useRef } from 'react';
import { useParams, useLocation } from '@thd-olt-component-react/router';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { Row, Col } from '@thd-olt-component-react/grid';
import { ExperienceContext, useStore, useStoreId } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Metadata, PackagesMetadata } from '@thd-olt-component-react/metadata';
import { Price } from '@thd-olt-component-react/price';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { PackageRedirector } from '@thd-nucleus/app-render';
import {
  OrderAPackage, OrderAPackageLoader
} from '@thd-olt-component-react/order-a-package';
import {
  params,
  shape,
  extend,
  string,
  number,
  bool,
  arrayOf,
  client,
  useDataModel,
  QueryProvider
} from '@thd-nucleus/data-sources';
import { TempBreadcrumbs } from '../../tempComponents/TempBreadcrumbs';
import {
  triggerPackageAnalytics,
} from '../../utils/digitalDataUtils';
import { GM_PACKAGE } from '../../constants';
import '../../styles/package-page.scss';

export const Package = () => {
  const { packageId } = useParams();
  const routerParams = useParams();
  const storeId = useStoreId();
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const { isLocalized, membershipInformation } = useStore({ varnish: disableSSR, online: false });
  const location = useLocation();
  const localizedAndClientLoaded = useRef(false);
  const { channel } = useContext(ExperienceContext);
  const {
    isCustomerIdentified
  } = useThdCustomer() || {};
  const isBrandPricingPolicyCompliant = localizedAndClientLoaded.current ? isCustomerIdentified : false;

  const isMobile = channel === 'mobile';
  const storeRef = useRef(storeId);
  const opts = {
    variables: {
      packageId,
      storeId,
      isBrandPricingPolicyCompliant,
      loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership,
    },
    context: {
      withAuth: true
    },
    ssr: true
  };

  const {
    data, loading, error, variables, responseLoading
  } = useDataModel('packages', opts);

  if (variables.storeId && data && !loading && isLocalized) {
    storeRef.current = variables.storeId;
  }

  if (data && !loading && !responseLoading && isLocalized) {
    localizedAndClientLoaded.current = true;
  }

  const metadata = data?.packages?.[0]?.metadata || {};
  const packageName = metadata?.name
  const isGMPackage = metadata?.packageCategory === GM_PACKAGE;
  let breadCrumbs = isGMPackage ? [...metadata.taxonomy.breadCrumbs] : undefined;
  if (breadCrumbs) {
    breadCrumbs[breadCrumbs.length - 1] = { label: packageName, url: location.pathname };
  }

  useEffect(() => {
    triggerPackageAnalytics(data?.packages, storeId, metadata, breadCrumbs, packageId, isCustomerIdentified);
  }, [data?.packages]);

  if (loading && !data) {
    return (
      <Col className="order-a-package-component--loader">
        <Col xs="10">
          <OrderAPackageLoader isMobile={isMobile} />
        </Col>
      </Col>
    );
  }

  return (
    <>
      <ErrorBoundary name="redirector">
        <PackageRedirector />
      </ErrorBoundary>
      <ErrorBoundary name="package-metadata">
        <QueryProvider
          cacheKey="packages-metadata"
          dataSource="packages"
          defaultVariables={{
            isBrandPricingPolicyCompliant
          }}
        >
          <Metadata>
            <PackagesMetadata params={routerParams} />
          </Metadata>
        </QueryProvider>
      </ErrorBoundary>
      <Row className="isBound">
        <Col>
          <ErrorBoundary name="breadcrumbs">
            { isGMPackage
              && <Breadcrumbs staticCrumbs={breadCrumbs} />}
            { !isGMPackage
              && <TempBreadcrumbs metadata={metadata} packageId={packageId} />}
          </ErrorBoundary>
        </Col>
        <Col>
          <ErrorBoundary name="order-a-package">
            <QueryProvider
              cacheKey="order-a-package"
              defaultVariables={{
                isBrandPricingPolicyCompliant
              }}
            >
              <OrderAPackage
                className="order-a-package"
                storeId={storeRef.current}
                data={data}
                loading={loading}
                error={error}
                packageId={packageId}
              />
            </QueryProvider>
          </ErrorBoundary>
        </Col>
      </Row>
    </>
  );
};

Package.displayName = 'PackagePage';

const ProductModel = extend({
  dataSources: string(),
  itemId: string(),
  details: shape({
    description: string()
  }),
  identifiers: shape({
    itemId: string(),
    canonicalUrl: string(),
    productLabel: string(),
    brandName: string(),
    modelNumber: string(),
    productType: string(),
    isSuperSku: bool(),
    parentId: string()
  }),
  info: shape({
    label: string(),
    productGroup: string(),
  }),
  media: shape({
    image: shape({
      url: string()
    }).client(),
    images: arrayOf({
      subType: string(),
      url: string(),
      type: string(),
      sizes: arrayOf(string())
    })
  }),
  reviews: shape({
    ratingsReviews: shape({
      averageRating: number(),
      totalReviews: number()
    })
  }),
  pricing: params({ storeId: string() }).shape({
    value: number(),
    mapAboveOriginalPrice: bool(),
    promotionalAdjustments: shape({
      type: string(),
      description: shape({
        shortDesc: string(),
        longDesc: string()
      }),
      dollarOff: number({ float: true }),
      percentageOff: number({ float: true }),
      promoId: string(),
    }),
  }),
  favoriteDetail: client(shape({
    count: number()
  })),
  keyProductFeatures: shape({
    keyProductFeaturesItems: arrayOf(
      shape({
        features: arrayOf(
          shape({
            name: string(),
            refinementId: string(),
            refinementUrl: string(),
            value: string(),
          }))
      }))
  }),
},
AddToList.dataModel.product,
KeyProductFeatures.dataModel.product,
Price.dataModel.product
);

Package.dataModel = extend({
  packages:
    params({ packageId: string().isRequired(), storeId: string().isRequired() })
      .arrayOf(
        shape({
          metadata: shape({
            packageId: string(),
            title: string(),
            brandName: string(),
            brandImage: shape({ url: string() }),
            heroImage: shape({ url: string() }),
            image: shape({ url: string() }),
            name: string(),
            packageCategory: string(),
            taxonomy: shape({ breadCrumbs: shape({ url: string(), label: string() }) })
          }),
          products: params().arrayOf(ProductModel)
        }))
},
OrderAPackage.dataModel,
Breadcrumbs.dataModel
);
